/**
 * Embed form-effect — handles iframe auto-resize via ResizeObserver and
 * popup/slider trigger messaging to the parent frame.
 * SSR-safe: returns undefined if document is not available.
 */

import type { FormEffectHandler } from '@atlas-forms/types-js';
import { str } from './utils';

export const embedEffect: FormEffectHandler = (config) => {
  if (typeof document === 'undefined') return;

  const embedType = str(config.embedType) || 'inline';
  const iframeAutoResize = config.iframeAutoResize !== false;

  let resizeObserver: ResizeObserver | undefined;
  let popupTimer: ReturnType<typeof setTimeout> | undefined;
  let scrollListener: (() => void) | undefined;
  let mouseLeaveListener: ((e: MouseEvent) => void) | undefined;

  // Iframe auto-resize via ResizeObserver
  if (iframeAutoResize && typeof ResizeObserver !== 'undefined') {
    const postSize = () => {
      window.parent.postMessage(
        { type: 'atlas-forms:resize', height: document.documentElement.scrollHeight },
        '*',
      );
    };

    resizeObserver = new ResizeObserver(postSize);
    resizeObserver.observe(document.body);

    // Post initial size immediately
    postSize();
  }

  // Popup / slider trigger
  if (embedType === 'popup' || embedType === 'slider') {
    const popupTrigger = str(config.popupTrigger) || 'load';
    const popupDelaySeconds = Number(config.popupDelaySeconds ?? 0);

    const showPopup = () => {
      window.parent.postMessage({ type: 'atlas-forms:show-popup' }, '*');
    };

    switch (popupTrigger) {
      case 'load':
        showPopup();
        break;

      case 'delay':
        popupTimer = setTimeout(showPopup, popupDelaySeconds * 1000);
        break;

      case 'scroll':
        scrollListener = () => {
          if (window.scrollY > 100) {
            showPopup();
            window.removeEventListener('scroll', scrollListener!);
            scrollListener = undefined;
          }
        };
        window.addEventListener('scroll', scrollListener);
        break;

      case 'exit':
        mouseLeaveListener = (e: MouseEvent) => {
          if (e.clientY <= 0) {
            showPopup();
            document.removeEventListener('mouseleave', mouseLeaveListener!);
            mouseLeaveListener = undefined;
          }
        };
        document.addEventListener('mouseleave', mouseLeaveListener);
        break;

      default:
        break;
    }
  }

  return () => {
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
    if (popupTimer !== undefined) {
      clearTimeout(popupTimer);
    }
    if (scrollListener) {
      window.removeEventListener('scroll', scrollListener);
    }
    if (mouseLeaveListener) {
      document.removeEventListener('mouseleave', mouseLeaveListener);
    }
  };
};
